import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Building2, Check, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/constants";
import { HINT_KEY } from "@/components/tracker/WorkspaceSwitcher";

// Pending workspace invites for the signed-in email, shown as cards above every page.
export function InviteInbox() {
  const { user, switchWorkspace } = useAuth();
  const navigate = useNavigate();
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => api.get("/invitations/mine").then((r) => setInvites(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    window.addEventListener("focus", load);
    const t = setInterval(load, 60000);
    return () => { window.removeEventListener("focus", load); clearInterval(t); };
  }, [load, user?.workspace_id]);

  const accept = async (inv) => {
    setBusy(inv.id);
    const from = user?.workspace_name;
    try {
      const { data } = await api.post(`/invitations/${inv.token}/accept`);
      setInvites((xs) => xs.filter((x) => x.id !== inv.id));
      const name = data.workspace_name || inv.workspace_name;
      try {
        localStorage.setItem(HINT_KEY, from || "your other workspace");
        await switchWorkspace(data.workspace_id);
      } catch {
        localStorage.removeItem(HINT_KEY);
        toast.success(`You joined ${name}`, { description: "Open it from the workspace switcher at the top of the sidebar.", duration: 12000 });
        return;
      }
      navigate("/app");
      toast.success(`You're now in ${name}`, { duration: 5000 });
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    } finally {
      setBusy(null);
    }
  };

  const decline = async (inv) => {
    setBusy(inv.id);
    try {
      await api.post(`/invitations/${inv.token}/decline`);
      setInvites((xs) => xs.filter((x) => x.id !== inv.id));
      toast.success("Invitation declined");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <AnimatePresence initial={false}>
      {invites.map((inv) => (
        <motion.div
          key={inv.id}
          layout
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, height: 0, marginBottom: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="mb-6 flex flex-col gap-4 rounded-2xl border border-[#7CC4FF]/30 bg-[#7CC4FF]/[0.06] px-5 py-4 sm:flex-row sm:items-center"
          data-testid={`invite-card-${inv.id}`}
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#7CC4FF]/15 text-[#7CC4FF]"><Building2 className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-white" data-testid={`invite-card-title-${inv.id}`}>
              {inv.invited_by || "Someone"} invited you to join {inv.workspace_name || "their workspace"}
            </p>
            <p className="text-xs text-white/50">As {inv.role === "admin" ? "an admin" : "a member"} · same email and password, nothing new to set up.</p>
          </div>
          <div className="flex shrink-0 gap-2">
            <button onClick={() => decline(inv)} disabled={busy === inv.id} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs font-bold text-white/70 transition-colors duration-200 hover:bg-white/5 hover:text-white disabled:opacity-50" data-testid={`invite-decline-${inv.id}`}>
              <X className="h-3.5 w-3.5" /> Decline
            </button>
            <button onClick={() => accept(inv)} disabled={busy === inv.id} className="inline-flex items-center gap-1.5 rounded-full bg-[#00FF66] px-4 py-2 text-xs font-bold text-black transition-colors duration-200 hover:bg-[#00cc52] disabled:opacity-50" data-testid={`invite-accept-${inv.id}`}>
              <Check className="h-3.5 w-3.5" /> {busy === inv.id ? "Joining…" : "Accept & switch"}
            </button>
          </div>
        </motion.div>
      ))}
    </AnimatePresence>
  );
}
